/**
 * Program and Provider Import Manager
 * Unified dashboard for all data imports - providers, CBOs, and program intelligence
 *
 * Tabs:
 * - Provider Import: Import healthcare providers from CMS NPPES, HRSA
 * - CBO Import: Import community-based organizations
 * - Program Import: Import program intelligence from CMS, HRSA, state filings
 * - ML Data Sources: Configure ML feature store data sources
 * - Plan Comparison: Plan comparison data management
 * - Member Enrichment: Member-level enrichment dashboard, module config, eligibility rules
 * - Provider Enrichment: AI enrichment, web scraping, social media across providers/CBOs/programs
 * - ETL Pipeline: Job history, scheduled imports, orchestration, geocoding
 * - Analytics: Cost trends and smart recommendations
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First & Desktop-Ready)
 */

import React, { useState } from 'react';
import { Upload, Building2, BarChart3, FileText, Play, Sparkles, Brain, Scale, Users, Radio } from 'lucide-react';
import ImportManagerDashboard, { IntentSourcesSection } from './ImportManagerDashboard';
import IntelligenceImportManager from './IntelligenceImportManager';
import AnalyticsDashboard from './AnalyticsDashboard';
import CBOImportManager from './CBOImportManager';
import ETLDashboard from './ETLDashboard';
import UnifiedEnrichmentManager from './UnifiedEnrichmentManager';
import MLDataSourcesTab from './MLDataSourcesTab';
import PlanComparisonDataTab from './PlanComparisonDataTab';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';

const VALID_TABS = [
  'providers', 'cbo-import', 'program-import', 'intent-sources',
  'ml-data', 'plan-comparison', 'enrichment', 'etl-pipeline', 'analytics',
];

const ImportManagerEnhanced = () => {
  const [activeTab, setActiveTab] = useState('providers');
  useSubtabNavigation(setActiveTab, VALID_TABS);

  // Grouped into categories. "Provider Data" keeps provider import, CBO import,
  // and provider enrichment adjacent; other tabs stay as-is.
  const tabGroups = [
    {
      category: 'Provider Data',
      tabs: [
        { id: 'providers', label: 'Provider Import', icon: <Upload className="h-4 w-4" /> },
        { id: 'cbo-import', label: 'CBO Import', icon: <Building2 className="h-4 w-4" /> },
        { id: 'enrichment', label: 'Provider Enrichment', icon: <Sparkles className="h-4 w-4" /> },
      ],
    },
    {
      category: 'Programs & Sources',
      tabs: [
        { id: 'program-import', label: 'Program Import', icon: <FileText className="h-4 w-4" /> },
        { id: 'intent-sources', label: 'Intent Sources', icon: <Radio className="h-4 w-4" /> },
        { id: 'ml-data', label: 'ML Data Sources', icon: <Brain className="h-4 w-4" /> },
        { id: 'plan-comparison', label: 'Plan Comparison', icon: <Scale className="h-4 w-4" /> },
      ],
    },
    {
      category: 'Pipeline',
      tabs: [
        { id: 'etl-pipeline', label: 'ETL Pipeline', icon: <Play className="h-4 w-4" /> },
        { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="h-4 w-4" /> },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tab Navigation */}
      <div className="bg-surface px-4 md:px-6 lg:px-8 py-4 md:py-6 shadow-md">
        {/* Page title rendered by the shell chrome */}
        {/* Tabs - grouped by category; scrollable on mobile */}
        <div className="flex items-end gap-3 overflow-x-auto pb-2 border-b-2 border-gray-200 -mx-4 px-4 md:mx-0 md:px-0">
          {tabGroups.map((group, gi) => (
            <React.Fragment key={group.category}>
              {gi > 0 && <div className="self-stretch w-px bg-gray-200 mx-1 flex-shrink-0" />}
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-3 mb-0.5 whitespace-nowrap hidden sm:block">
                  {group.category}
                </span>
                <div className="flex gap-1">
                  {group.tabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`flex items-center gap-2 px-3 md:px-4 py-2.5 border-b-3 font-medium text-sm whitespace-nowrap transition-all ${
                        activeTab === tab.id
                          ? 'text-blue-600 border-b-blue-600 bg-blue-50'
                          : 'text-gray-600 border-b-transparent hover:text-blue-600 hover:bg-blue-50/50'
                      }`}
                      style={{ borderBottomWidth: '3px', marginBottom: '-2px' }}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <span className="flex-shrink-0">{tab.icon}</span>
                      <span className="hidden sm:inline font-semibold">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'providers' && <ImportManagerDashboard />}
        {activeTab === 'cbo-import' && <CBOImportManager />}
        {activeTab === 'program-import' && <IntelligenceImportManager />}
        {activeTab === 'intent-sources' && (
          <div className="p-6">
            <IntentSourcesSection />
          </div>
        )}
        {activeTab === 'ml-data' && (
          <div className="p-6">
            <MLDataSourcesTab />
          </div>
        )}
        {activeTab === 'plan-comparison' && (
          <div className="p-6">
            <PlanComparisonDataTab />
          </div>
        )}
        {activeTab === 'enrichment' && <UnifiedEnrichmentManager />}
        {activeTab === 'etl-pipeline' && <ETLDashboard />}
        {activeTab === 'analytics' && <AnalyticsDashboard />}
      </div>
    </div>
  );
};

export default ImportManagerEnhanced;
